import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bug,
  Eye,
  HeartPulse,
  AlertTriangle,
  XCircle,
  CircleCheck,
  Calendar,
  ImageIcon,
  Ticket,
} from 'lucide-react';

type HealthLevel = 'healthy' | 'attention' | 'problem';

interface HostHealthData {
  level: HealthLevel;
  errors7d: number;
  warnings7d: number;
  activityCount: number;
  bookingCount: number;
  hasCoverImage: boolean;
}

function useHostHealthSummary(producerId: string) {
  return useQuery({
    queryKey: ['host-health-summary', producerId],
    staleTime: 60_000,
    queryFn: async (): Promise<HostHealthData> => {
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const sinceISO = since.toISOString();

      const [eventsRes, activitiesRes, bookingsRes, mediaRes] = await Promise.all([
        supabase
          .from('system_events')
          .select('level')
          .eq('producer_id', producerId)
          .in('level', ['error', 'warn'])
          .gte('created_at', sinceISO),
        supabase
          .from('producer_events')
          .select('id', { count: 'exact', head: true })
          .eq('producer_id', producerId),
        supabase
          .from('event_bookings')
          .select('id', { count: 'exact', head: true })
          .eq('producer_id', producerId)
          .neq('status', 'cancelled'),
        supabase
          .from('media_assets')
          .select('id', { count: 'exact', head: true })
          .eq('owner_type', 'producer')
          .eq('owner_id', producerId)
          .eq('kind', 'cover'),
      ]);

      const errors7d = (eventsRes.data || []).filter(e => e.level === 'error').length;
      const warnings7d = (eventsRes.data || []).filter(e => e.level === 'warn').length;
      const activityCount = activitiesRes.count ?? 0;
      const bookingCount = bookingsRes.count ?? 0;
      const hasCoverImage = (mediaRes.count ?? 0) > 0;

      let level: HealthLevel = 'healthy';
      if (errors7d > 0) level = 'problem';
      else if (warnings7d > 0 || !hasCoverImage || activityCount === 0) level = 'attention';

      return { level, errors7d, warnings7d, activityCount, bookingCount, hasCoverImage };
    },
  });
}

function LevelBadge({ level }: { level: HealthLevel }) {
  switch (level) {
    case 'healthy':
      return (
        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
          <CircleCheck className="h-3 w-3 mr-1" /> Frisk
        </Badge>
      );
    case 'attention':
      return (
        <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3 mr-1" /> Behöver uppmärksamhet
        </Badge>
      );
    case 'problem':
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" /> Problem
        </Badge>
      );
  }
}

interface Props {
  producerId: string;
}

export function HostHealthSummaryCard({ producerId }: Props) {
  const { data, isLoading } = useHostHealthSummary(producerId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeartPulse className="h-4 w-4" />
          Host Health
          {data && <LevelBadge level={data.level} />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-36" />
          </div>
        ) : data ? (
          <div className="space-y-3">
            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat
                icon={XCircle}
                label="Fel (7d)"
                value={data.errors7d}
                alert={data.errors7d > 0}
              />
              <Stat
                icon={AlertTriangle}
                label="Varningar (7d)"
                value={data.warnings7d}
                warn={data.warnings7d > 0}
              />
              <Stat icon={Calendar} label="Aktiviteter" value={data.activityCount} />
              <Stat
                icon={Ticket}
                label="Bokningar"
                value={data.bookingCount}
                fallback={data.bookingCount === 0 ? 'inga ännu' : undefined}
              />
            </div>

            {/* Cover image status */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              {data.hasCoverImage ? 'Omslagsbild finns' : 'Omslagsbild saknas'}
            </div>

            {/* Quick actions */}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/app/admin/system/host-debug?q=${producerId}`}>
                  <Bug className="h-3.5 w-3.5 mr-1.5" />
                  Host Debug
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a
                  href={`/app/producer/farm/${producerId}?support_preview=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Visa som värd
                </a>
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  alert,
  warn,
  fallback,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  alert?: boolean;
  warn?: boolean;
  fallback?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon
        className={`h-3.5 w-3.5 ${
          alert ? 'text-destructive' : warn ? 'text-amber-500' : 'text-muted-foreground'
        }`}
      />
      <div>
        <span className={`font-medium ${alert ? 'text-destructive' : ''}`}>
          {fallback || value}
        </span>
        <span className="text-muted-foreground ml-1 text-xs">{label}</span>
      </div>
    </div>
  );
}
